import { useCallback, useMemo } from 'react';
import { IoIosList } from 'react-icons/io';
import { IoBarChartOutline, IoPersonOutline } from 'react-icons/io5';
import { Box } from '@ghq-abi/design-system';
import { GearIcon, HouseIcon } from '@ghq-abi/design-system-icons';
import { useTranslate } from '@tolgee/react';

import { useAbility } from '~/app/contexts/AbilityContext';
import { useFlags } from '~/app/contexts/FlagsContext';
import { RouteObject } from '~/app/types';
import { Question } from '~/shared/components/icons/Question';
import { PagesPathsKeys } from '~/shared/constants/pages';
import { canShowAdminEntry } from '~/shared/utils/adminPanel';

export function useMenuRoutes() {
    const { t } = useTranslate();
    const ability = useAbility();
    const flags = useFlags() ?? ({} as Record<string, { active?: boolean }>);

    const hasAuthorizedFlags = useCallback(
        (authorizedFlags: PagesPathsKeys | PagesPathsKeys[]) => {
            if (typeof authorizedFlags === 'string' && !flags[authorizedFlags]?.active) {
                return false;
            }
            if (
                Array.isArray(authorizedFlags) &&
                !authorizedFlags.some((flag) => flags[flag]?.active)
            ) {
                return false;
            }
            return true;
        },
        [flags],
    );

    const powerBIDashboardUrl = 'https://app.powerbi.com/groups/27875dc9-dcaa-444f-948f-fe8f301c4ea1/reports/01228637-3caf-4882-85b6-cdbf56347504/9806a7e3b69cb840cd55?ctid=cef04b19-7776-4a94-b89b-375c77a8f936&experience=power-bi'
    const helpDeskUrl = 'https://abinbevww.service-now.com/cheers?id=cheers_hub_cat_item&sys_id=5ad2235adb1cc050f17eb29f29961934';
    const raiseServiceRequestUrl = 'https://abinbevww.service-now.com/cheers?id=sc_cat_item&sys_id=05dbb0960f0403109bd471d800d1b29a';

    const { menuRoutes, footerRoutes } = useMemo(() => {
        const menuRoutesList: RouteObject[] = [];
        const footerRoutesList: RouteObject[] = [];

        // Common routes for all profiles
        if (hasAuthorizedFlags('home') && ability.can('view', 'Home')) {
            menuRoutesList.push({
                key: 'home',
                icon: <HouseIcon />,
                path: '/home',
                title: t('common.navbar.home'),
            });
        }

        if (hasAuthorizedFlags('myRequests') && ability.can('view', 'MyRequests')) {
            menuRoutesList.push({
                key: 'My Requests',
                icon: <IoIosList />,
                path: '/my-requests',
                title: t('common.navbar.my_requests'),
            });
        }

        if (
            hasAuthorizedFlags('resources') &&
            ability.can('view', 'Resources')
        ) {
            menuRoutesList.push({
                key: 'Resources',
                icon: <IoPersonOutline />,
                path: '/resources',
                title: t('common.navbar.resources'),
            });
        }

        if (
            canShowAdminEntry({
                hasAdminFlag: hasAuthorizedFlags('admin'),
                hasAdminAccess: ability.can('view', 'Admin'),
            })
        ) {
            menuRoutesList.push({
                key: 'Admin',
                icon: <GearIcon />,
                path: '/admin',
                title: t('common.navbar.admin'),
            });
        }

        menuRoutesList.push({
            key: 'powerBIDashboard',
            icon: <IoBarChartOutline />,
            path: powerBIDashboardUrl,
            title: 'PowerBI Dashboard',
            external: true,
        });

        footerRoutesList.push({
            key: 'help',
            title: t('common.navbar.helpCenter'),
            icon: (
                <Box
                    css={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: '#F2DC39',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Question size={42} circleColor="#191F2E" textColor="#191F2E" />
                </Box>
            ),
            routeComponents: [
                {
                    key: 'raiseAServiceRequest',
                    title: t('common.navbar.raiseAServiceRequest', 'Raise a Service Request (Roles & Permissions)'),
                    path: raiseServiceRequestUrl,
                    external: true,
                },
                {
                    key: 'isSomethingNotWorking',
                    title: t('common.navbar.isSomethingNotWorking'),
                    path: helpDeskUrl,
                    external: true,
                },
            ],
        });

        return { menuRoutes: menuRoutesList, footerRoutes: footerRoutesList };
    }, [ability, hasAuthorizedFlags, t]);

    return { menuRoutes, footerRoutes };
}
